/**
 * <About /> — the /about page, and the AboutPage and Person schema with it.
 *
 * ONE SOURCE, TWO OUTPUTS
 *   The visible page and the structured data are both built from
 *   src/content/about.js, in the same render. This page makes claims about
 *   two real, named people, so a schema field that says something the page
 *   does not say is worse here than anywhere else on the site: it is
 *   scraped, repeated, and outlives any correction. There is one copy of
 *   every sentence. Never paste a bio into buildAboutJsonLd().
 *
 * WHERE THE FOUNDER INSTAGRAM GOES
 *   On both Person nodes, never on Organization. The company account and
 *   the founders' behind-the-scenes account are different entities, and
 *   putting a personal account on the company node blurs the two. See
 *   yedding-brain/BRAND-CONTEXT.md §8.1. scripts/check-about.js reads / and
 *   fails if the founder account has leaked onto Organization.
 *
 * THE FOUNDER CARD WITHOUT A PHOTO
 *   No photo exists yet. The card is designed text-first on purpose: no
 *   placeholder box, no grey silhouette, no empty frame. A card that looks
 *   like it is missing something is worse than a card that never promised
 *   a picture. When `photo` appears on a record in src/content/about.js
 *   the card grows an <img> and the Person node grows an `image`, with no
 *   change to this file.
 *
 * WHY THE HEADINGS ARE SHAPED THIS WAY
 *   <h1> comes from <Page>. Each section is a <section> with an <h2>, and
 *   each founder is an <h3> inside the people section. That gives a screen
 *   reader and a crawler the same two-level outline the schema describes.
 *
 * See docs/aeo/AEO-LEDGER.md, task 11a.
 */

import styled from 'styled-components';

import Page from '../Page';
import Seo from '../Seo';
import Footer from '../Shared/NewFooter';
import Header from '../Shared/NewHeader';
import about, { founders } from '../../content/about';

import { colors, ContentWrapper, mobile, PageWrapper, tablet } from '../styles';

const ORIGIN = 'https://yedding.com';
const ORG_ID = `${ORIGIN}/#organization`;

/**
 * One Person node per founder, built from the same record the card renders.
 *
 * Only the fields yedding-brain/BRAND-CONTEXT.md §8.2 approves: name, jobTitle,
 * worksFor, sameAs, and image once a photo exists. No address, no email,
 * no birth date. Adding a field here is a privacy decision, not a schema
 * decision, and it belongs in §8.2 first.
 */
const buildPerson = (person) => ({
  '@type': 'Person',
  '@id': `${ORIGIN}/#${person.id}`,
  name: person.name,
  jobTitle: person.jobTitle,
  worksFor: { '@id': ORG_ID },
  sameAs: [person.sameAs],
  ...(person.photo ? { image: `${ORIGIN}${person.photo.src}` } : {})
});

/**
 * The AboutPage graph.
 *
 * Exported so scripts/check-about.js can compare it with the rendered text
 * without reaching into the component.
 */
export const buildAboutJsonLd = () => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': `${ORIGIN}/about#webpage`,
      name: about.title,
      url: `${ORIGIN}/about`,
      description: about.intro,
      about: { '@id': ORG_ID },
      mainEntity: { '@id': ORG_ID }
    },
    ...founders.map(buildPerson)
  ]
});

const Wrapper = styled(ContentWrapper)`
  & > main {
    max-width: 908px;
    margin: 0 auto;
    padding: 40px 44px 60px;

    @media (max-width: ${tablet}) {
      padding: 32px 24px 48px;
    }

    @media (max-width: ${mobile}) {
      padding: 24px 15px 40px;
    }
  }
`;

// The type scale is 34 / 20 / 16 and nothing else, matching src/components/Faq
// and src/components/Legal exactly. A fifth size on a third page is how three
// content pages start to look like three different websites. Measured
// character for character against /terms and /faq on 2026-09-20.
const Article = styled.div`
  color: ${colors.defaultText};
  font-size: 16px;
  line-height: 1.65;

  @media (max-width: ${mobile}) {
    font-size: 15px;
  }

  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 36px 0 12px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 2px;
  }

  p {
    margin: 0 0 14px;
  }

  a {
    color: ${colors.defaultText};
    text-decoration: underline;
  }
`;

const Intro = styled.p`
  color: ${colors.fadedText};
  margin: 0 0 8px !important;
`;

// A numbered list that keeps the lead sentence bold and on its own line, so
// one point can be quoted alone and still make sense. That is how an answer
// engine uses a list like this.
const Points = styled.ol`
  margin: 0 0 14px;
  padding-left: 20px;

  li {
    margin: 0 0 12px;
  }

  strong {
    font-weight: 600;
  }
`;

// Text-first by design. No fixed height and no image placeholder, so the
// card is complete with nothing missing. `Photo` below is the whole of what
// changes when a picture arrives.
const People = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  margin: 20px 0 14px;

  @media (max-width: ${mobile}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const Card = styled.article`
  border: 1px solid ${colors.primaryBorder};
  border-radius: 8px;
  padding: 20px 22px;

  @media (max-width: ${mobile}) {
    padding: 18px;
  }

  p {
    margin: 8px 0 0;
  }
`;

const Role = styled.p`
  color: ${colors.fadedText};
  margin: 0 !important;
  font-size: 15px;
`;

const Photo = styled.img`
  width: 100%;
  height: auto;
  border-radius: 6px;
  margin: 0 0 14px;
  display: block;
`;

/** A sentence with one link in the middle of it. Never "click here". */
const Linked = ({ block, external }) => (
  <p>
    {block.before}
    <a
      href={block.link.href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {block.link.text}
    </a>
    {block.after}
  </p>
);

const FounderCard = ({ person }) => (
  <Card>
    {person.photo ? (
      <Photo src={person.photo.src} alt={person.photo.alt} width="400" height="400" />
    ) : null}
    <h3>{person.name}</h3>
    <Role>{person.jobTitle}</Role>
    <p>{person.bio}</p>
  </Card>
);

const Section = ({ section }) => (
  <section aria-labelledby={section.id}>
    <h2 id={section.id}>{section.heading}</h2>

    {(section.paragraphs || []).map((text) => (
      <p key={text.slice(0, 40)}>{text}</p>
    ))}

    {section.points ? (
      <Points>
        {section.points.map((point) => (
          <li key={point.id}>
            <strong>{point.lead}</strong> {point.text}
          </li>
        ))}
      </Points>
    ) : null}

    {section.founders ? (
      <People>
        {section.founders.map((person) => (
          <FounderCard key={person.id} person={person} />
        ))}
      </People>
    ) : null}

    {section.instagram ? <Linked block={section.instagram} external /> : null}
    {section.company ? <Linked block={section.company} external /> : null}
    {section.outro ? <Linked block={section.outro} /> : null}

    {section.contact ? (
      <p>
        {'Email '}
        <a href={`mailto:${section.contact.email}`}>{section.contact.email}</a>
        {' or call '}
        <a href={`tel:${section.contact.phoneHref}`}>{section.contact.phone}</a>
        {'.'}
        {section.contact.after}
      </p>
    ) : null}
  </section>
);

const About = () => (
  <PageWrapper>
    <Seo jsonLd={buildAboutJsonLd()} />
    <Header omitSearch />
    <Wrapper>
      <Page title={about.title}>
        <Article>
          <Intro>{about.intro}</Intro>
          {about.sections.map((section) => (
            <Section key={section.id} section={section} />
          ))}
        </Article>
      </Page>
    </Wrapper>
    <Footer />
  </PageWrapper>
);

export default About;
